import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import Cookies from "js-cookie";
import MainContainer from "../MainContainer";
import { ucFirst } from "../../helpers/generic";
import successAlert from "../../helpers/successAlert";
import failAlert from "../../helpers/failAlert";

const authHeaders = () => ({ Authorization: `Bearer ${Cookies.get("access_token")}` });

export default function NotificationPreferences() {
    const [statuses, setStatuses]   = useState([]);
    const [prefs, setPrefs]         = useState(null);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);
    const [saving, setSaving]       = useState(false);
    const isMounted                 = useRef(true);

    useEffect(() => {
        isMounted.current = true;

        // Fetch preferences and statuses in parallel.
        // Statuses come from the existing /api/statuses endpoint (already used by the rest of the app).
        Promise.all([
            axios.get("/api/notification-preferences", { headers: authHeaders() }),
            axios.get("/api/statuses", { headers: authHeaders() }),
        ])
            .then(([prefsRes, statusesRes]) => {
                if (!isMounted.current) return;

                // Preferences response: { preferences: {...} }
                const rawPrefs = prefsRes?.data?.preferences ?? prefsRes?.data ?? null;

                // Statuses response: Laravel ResourceCollection → { data: [...] }
                // but also handles a plain array fallback
                const rawStatuses = Array.isArray(statusesRes?.data?.data)
                    ? statusesRes.data.data
                    : Array.isArray(statusesRes?.data)
                    ? statusesRes.data
                    : [];

                setPrefs(rawPrefs);
                setStatuses(rawStatuses);
                setLoading(false);
            })
            .catch(err => {
                if (!isMounted.current) return;
                console.error("Failed to load notification preferences:", err);
                setError("Failed to load preferences. Please refresh and try again.");
                setLoading(false);
            });

        return () => { isMounted.current = false; };
    }, []);

    const toggleBool = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

    const toggleStatus = (id) => {
        setPrefs(p => {
            // null means ALL – when user first deselects one, expand to explicit array
            const current = p.notify_status_ids === null
                ? statuses.map(s => s.id)
                : [...(p.notify_status_ids || [])];
            const next = current.includes(id)
                ? current.filter(x => x !== id)
                : [...current, id];
            // if every status is re-selected, collapse back to null (= all)
            return { ...p, notify_status_ids: next.length === statuses.length ? null : next };
        });
    };

    const isStatusChecked = (id) =>
        prefs?.notify_status_ids === null ||
        (Array.isArray(prefs?.notify_status_ids) && prefs.notify_status_ids.includes(id));

    const checkAll = () => {
        setPrefs(p => ({
            ...p,
            notify_assigned: true,
            notify_new_note: true,
            notify_new_reply: true,
            notify_status_ids: null, // null = all statuses
        }));
    };

    const uncheckAll = () => {
        setPrefs(p => ({
            ...p,
            notify_assigned: false,
            notify_new_note: false,
            notify_new_reply: false,
            notify_status_ids: [], // empty = no statuses
        }));
    };

    const handleSave = () => {
        setSaving(true);
        axios
            .post("/api/notification-preferences-update", prefs, { headers: authHeaders() })
            .then(() => {
                if (!isMounted.current) return;
                setSaving(false);
                successAlert('Notification preferences saved');
            })
            .catch(err => {
                if (!isMounted.current) return;
                setSaving(false);
                failAlert(err);
            });
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <MainContainer>
            <div className="section-container" style={{ paddingTop: "1.75rem" }}>
                <div className="ticket_sys_header mb-3">
                    <h4 className="table-name">Notification Preferences</h4>
                </div>

                {loading && (
                    <div className="labels-container-div justify-content-center">
                        <Spinner animation="border" role="status" />
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {!loading && !error && (

                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-2">

                    {/* 1. Assignment */}
                    <div className="col">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column justify-content-between align-items-start text-start">
                                <div>
                                    <strong>Assigned to a ticket</strong>
                                </div>
                                <div className="text-muted small mt-2">
                                    Notify me when I am assigned to a ticket.
                                </div>

                                <div className="border-top w-100 mt-3 pt-3 d-flex justify-content-start">
                                    <div
                                        className="form-check form-switch"
                                        style={{ transform: "scale(1.4)", transformOrigin: "left center" }}
                                    >
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={!!prefs?.notify_assigned}
                                            onChange={() => toggleBool("notify_assigned")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. New note */}
                    <div className="col">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column justify-content-between align-items-start text-start">
                                <div>
                                    <strong>New note on a ticket</strong>
                                </div>
                                <div className="text-muted small mt-2">
                                    Notify me when a new internal note is added.
                                </div>

                                <div className="border-top w-100 mt-3 pt-3 d-flex justify-content-start">
                                    <div
                                        className="form-check form-switch"
                                        style={{ transform: "scale(1.4)", transformOrigin: "left center" }}
                                    >
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={!!prefs?.notify_new_note}
                                            onChange={() => toggleBool("notify_new_note")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. New reply */}
                    <div className="col">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column justify-content-between align-items-start text-start">
                                <div>
                                    <strong>New reply on a ticket</strong>
                                </div>
                                <div className="text-muted small mt-2">
                                    Notify me when a reply is posted on a ticket.
                                </div>

                                <div className="border-top w-100 mt-3 pt-3 d-flex justify-content-start">
                                    <div
                                        className="form-check form-switch"
                                        style={{ transform: "scale(1.4)", transformOrigin: "left center" }}
                                    >
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={!!prefs?.notify_new_reply}
                                            onChange={() => toggleBool("notify_new_reply")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 4. Statuses */}
                    {statuses.map(s => (
                        <div key={s.id} className="col">
                            <div className="card h-100">
                                <div className="card-body d-flex flex-column justify-content-between align-items-start text-start">
                                    
                                    <div>
                                        <strong>Ticket status update</strong>
                                    </div>
                                    <div className="text-muted small mt-2">
                                        Notify me when a ticket is marked as{" "}
                                        <span className="d-inline-flex align-items-center gap-1">
                                            {s.color && (
                                                <span
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "50%",
                                                        background: s.color,
                                                        display: "inline-block",
                                                    }}
                                                />
                                            )}
                                            <strong>{ucFirst(s.name)}</strong>
                                        </span>.
                                    </div>

                                    <div className="border-top w-100 mt-3 pt-3 d-flex justify-content-start">
                                        <div
                                            className="form-check form-switch"
                                            style={{ transform: "scale(1.4)" }}
                                        >
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={isStatusChecked(s.id)}
                                                onChange={() => toggleStatus(s.id)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                )}
                <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                    <button
                        className="btn btn-primary"
                        onClick={checkAll}
                        disabled={loading || !!error}
                    >
                        Check All
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={uncheckAll}
                        disabled={loading || !!error}
                    >
                        Uncheck All
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving || loading || !!error}
                    >
                        {saving ? "Saving..." : "Save Preferences"}
                    </button>
                </div>
            </div>
        </MainContainer>
    );
}
